import React from 'react';
import styled from 'styled-components';
import { Box, tokens, getVariableName, Link, Inline, LoadingSpinner, Stack, Heading, Columns, Column, Icon, Text } from '@happy-design/react';
import { useTranslate } from '../../lib/translate.js';
import { useNavContext } from '../nav/Nav.js';
import { Customer } from './Customer.js';
import { ExperienceSwitcherProvider, useExperienceSwitcher } from './ExperienceSwitcherContext.js';
import { Modal } from './Modal.js';
import { ProductLine } from './ProductLine.js';
import { getProductLineUrl, getProductUrl } from './productUrl.js';

const ALL_PRODUCT_LINES = [
    "HAPPY_ASSET",
    "HAPPY_PROPERTY",
    "HAPPY_FORCE"
];
const SwitcherBox = styled(Box)`
  --hd-unified-nav-asset-primary: #fbbf24;
  --hd-unified-nav-asset-weak: #fef3c7;
  --hd-unified-nav-property-primary: #22d3ee;
  --hd-unified-nav-property-weak: #cffafe;
  --hd-unified-nav-force-primary: #a19dff;
  --hd-unified-nav-force-weak: #efefff;

  background-color: ${tokens.color.palette.white[100]};
  border-radius: 0 ${tokens.size["border-radius"].xl} ${tokens.size["border-radius"].xl} 0;
  box-shadow: 0px 2px 1rem rgba(0, 0, 0, 0.1);
  min-width: 19.75rem;
  max-width: 19.75rem;
  height: 100%;
  top: 0;
  position: absolute;
  ${getVariableName(tokens.color.text.default)}: ${tokens.color.palette.blurple[160]};
  color: ${tokens.color.text.default};
`;
const StyledLink = styled(Link)`
  text-decoration: none;
  &:hover,
  &:focus {
    color: ${tokens.color.text.default};
  }

  &.admin-center-link {
    border-radius: ${tokens.size["border-radius"].md};
    border: 1px solid transparent;
  }
  &.admin-center-link:hover {
    border: 1px solid ${tokens.color.accent.weak};
  }
  &.admin-center-link .link-icon {
    padding: 0;
    display: none;
  }
  &.admin-center-link:hover .link-icon {
    display: inline-flex;
  }
  &.admin-center-link .link-icon svg {
    stroke: ${tokens.color.palette.gray[80]};
    color: ${tokens.color.palette.gray[80]};
    stroke-width: 1.5;
  }
`;
const FooterBox = styled(Box)`
  position: absolute;
  bottom: 0;
  background: ${tokens.color.palette.white[100]};
  border-bottom-right-radius: ${tokens.size["border-radius"].xl};
`;
/**
 * @param onSignOut - Callback to sign out the user.
 */ const ExperienceSwitcher = (props)=>{
    return React.createElement(ExperienceSwitcherProvider, null, React.createElement(Modal, null, React.createElement(ExperienceSwitcherInner, {
        ...props
    }), props.children));
};
const ExperienceSwitcherInner = (props)=>{
    const t = useTranslate();
    const nav = useNavContext();
    const uxs = useExperienceSwitcher();
    if (!uxs?.activeCustomer?.productLines) {
        return React.createElement(SwitcherBox, null, React.createElement(Inline, {
            align: "center",
            alignY: "center",
            width: "fill",
            height: "fill"
        }, React.createElement(LoadingSpinner, {
            size: "medium",
            testId: "ExperienceSwitcher"
        })));
    }
    return React.createElement(SwitcherBox, null, React.createElement(Box, {
        style: {
            flex: "1"
        },
        height: "fill",
        maxWidth: 19.75
    }, React.createElement(Stack, {
        height: "fill"
    }, React.createElement(Box, {
        padding: [
            1.5,
            0.75,
            0
        ],
        overflow: "scroll",
        style: {
            flex: "1 1 auto",
            marginBottom: "5.125rem"
        }
    }, React.createElement(Stack, {
        gap: 0.25
    }, ALL_PRODUCT_LINES.map((productLineKey)=>{
        const productLine = uxs.activeCustomer?.productLines[productLineKey];
        if (!productLine) {
            return null;
        }
        return React.createElement(ProductLine, {
            key: productLineKey,
            id: productLineKey,
            // Expand the product line group if the active product is in this group
            expanded: !!productLine.products[nav.activeProduct],
            available: true
        });
    }), Object.keys(uxs.activeCustomer.productLines).length < ALL_PRODUCT_LINES.length && React.createElement(Box, {
        padding: [
            1.375,
            0,
            0.45,
            0.75
        ]
    }, React.createElement(Heading, {
        level: "4",
        color: "slateGrey80"
    }, t("nav:ExperienceSwitcher.discover"))), ALL_PRODUCT_LINES.map((productLine)=>{
        if (!uxs.activeCustomer.productLines[productLine]) {
            return React.createElement(StyledLink, {
                key: productLine,
                testId: `Discover-${productLine}`,
                to: getProductLineUrl(productLine)
            }, React.createElement(ProductLine, {
                id: productLine,
                expanded: false
            }));
        }
        return null;
    }), React.createElement(Box, {
        style: {
            flex: "1"
        }
    }), React.createElement(StyledLink, {
        testId: "Admin-Center",
        to: getProductUrl("ADMIN_CENTER", nav.businessId, nav.env),
        className: "admin-center-link"
    }, React.createElement(Box, {
        padding: [
            0.6,
            0.8,
            0.6,
            0.6
        ]
    }, React.createElement(Columns, {
        gap: 0.6,
        "aria-labelledby": "Switcher-Experience-Heading-Admin"
    }, React.createElement(Column, {
        span: "content"
    }, React.createElement(Icon, {
        icon: "settings",
        size: 1.5
    })), React.createElement(Column, {
        span: 1
    }, React.createElement(Inline, {
        alignY: "center"
    }, React.createElement(Text, {
        weight: "semibold"
    }, t("nav:ExperienceSwitcher.admin_center")))), React.createElement(Column, {
        span: "content"
    }, React.createElement(Inline, {
        alignY: "center"
    }, React.createElement(Icon, {
        className: "link-icon",
        icon: "external-link",
        size: "xsmall"
    })))))), React.createElement(Box, {
        padding: [
            0.5,
            0,
            0
        ]
    }))))), React.createElement(FooterBox, {
        width: "fill"
    }, React.createElement(Stack, null, React.createElement(Customer, {
        onSignOut: props.onSignOut
    }))));
};
ExperienceSwitcher.dataId = "data-hd-nav-exp-switcher";
ExperienceSwitcher.displayName = "ExperienceSwitcher";

export { ExperienceSwitcher };
